<script setup lang="ts">
import {
  type IService,
  type IServicePriceSettings,
  type IServicePriceSettingsParam,
  type ITarget,
  ServiceFormats,
  ServicePriceSettingsActions,
  ServiceTypes
} from '@types';
import { formatDay, formatWorkingTime } from '@sub/utils/times';
import { usePriceFormatter } from '@sub/utils/prices';

const props = withDefaults(defineProps<{
  service?: IService,
}>(), {
  service: () => ({} as IService),
});

const targets = computed(() => props.service?.targets?.reduce((res, t) => {
  res[t.id] = t;
  return res;
}, {} as {[id: string]: ITarget}) || {});
const showTargets = computed(() => Object.keys(targets.value).length > 1);

const show = defineModel<boolean>('show')

function getPricePeriodName(p: IServicePriceSettings) {
  switch (p.action) {
    case ServicePriceSettingsActions.Temporarily: return ['map.service.price.setting.action.temporarily', { start: formatDay(p.dateStart), end: formatDay(p.dateEnd) }];
    case ServicePriceSettingsActions.From: return ['map.service.price.setting.action.from', { text: formatDay(p.dateStart) }];
    case ServicePriceSettingsActions.Const: return ['map.service.price.setting.action.const'];
    default: return [''];
  }
}

const { formatPrice } = usePriceFormatter();

function getUnityByType(type: ServiceTypes) {
  switch (type) {
    case ServiceTypes.Individual: return 'word.person.short';
    default: return 'word.hour.short';
  }
}

function getUnityType(p: IServicePriceSettingsParam) {
  if (props.service.type === ServiceTypes.Individual) {
    return props.service.format !== ServiceFormats.timeAndPlace && {
      from: 'word.from.base',
      props: p.amountPeople,
      unity: getUnityByType(ServiceTypes.Individual),
    };
  }
  return {
    from: 'word.from.time.base',
    type: 'time',
    props: { h: p.durationHour, m: p.durationMinutes },
    unity: 'word.inclusive.short',
  };
}

const prices = computed(() => props.service.priceSettings?.map(price => ({
  ...price,
  id: useId(),
  label: getPricePeriodName(price),
  parameters: price.parameters.map(p => ({
    ...p,
    ...formatWorkingTime(p),
    unityType: getUnityType(p),
    priceText: formatPrice(p.amount),
    priceUnity: getUnityByType(props.service.type),
  })),
})));

function scrollTo(id: string) {
  const element = document.getElementById(`${id}`);
  if (element) element.scrollIntoView({ behavior: 'smooth' });
}
</script>

<template lang="pug">
UiModal(v-model:show="show" label="block.service.price.modal.base.title" maxWidth="700px" :full="false")
  template(v-slot:header-bottom)
    .typ-13-17-400.p-0-24
      .price-periods.flex.row-48.p-12-0.scrollbar-hide
        .price-link.white-space-nowrap(v-for="price of prices" :key="price.id" @click="scrollTo(price.id)") {{ $t(...price.label) }}
  template(v-slot:content)
    .flex.col-0.w-100p
      .price-content.flex.col-12.p-12-24(v-for="price of prices" :key="price.id")
        .secondary.typ-13-17-500 {{ $t(...price.label) }}
        .target.typ-13-17-500(v-if="showTargets && price.target") {{ targets[price.target.id].name }}
        .price-description.typ-11-14-500(v-for="(param, index) of price.parameters" :key="index")
          | {{ param.period.map($t).join(',') }},
          |  {{ param.timeText }} —
          |
          template(v-if="param.unityType")
            | {{ $t(param.unityType.from) }}
            |
            template(v-if="param.unityType.type === 'time'")
              TimeDuration(v-bind="param.unityType.props")
            template(v-else) {{ param.unityType.props }}
            |  {{ $t(param.unityType.unity) }} —
          |  {{ param.priceText }}/{{ $t(param.priceUnity).toLowerCase() }}
</template>

<style scoped lang="scss">
.secondary {
  color: #5B646D;
}

.target {
  color: #1F1F1F;
}

.price-periods {
  overflow: auto;
}

.price-link {
  color: #3870FF;
  cursor: pointer;
}

.price-description {
  color: #818D98;
}

.price-content {
  border-bottom: 1px solid #E5E5E9;
}
</style>